import {
    AfterContentChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import { isDefined } from '@evorait/pace-core';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import {
    collapseAccordionByGroup,
    collapseAccordionWithChildren,
    expandAccordionByGroup,
    expandAccordionWithChildren,
} from '../../store/actions/form.actions';
import { FormsState } from '../../store/state/pace-forms.state';

// Used to disable accordion toggle events firing within that amount of milliseconds after initial event
const debounceTime = 250;

@Component({
    selector: 'pace-forms-accordion',
    templateUrl: './forms-accordion.component.html',
    styleUrls: ['./forms-accordion.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FormsAccordionComponent implements OnChanges, AfterContentChecked {
    @ContentChild('heading') public heading: TemplateRef<HTMLDivElement>;
    @ContentChild('actionButton') public actionButton: TemplateRef<HTMLDivElement>;
    @ContentChild('body') public body: TemplateRef<HTMLDivElement>;
    @ContentChild('closer') public closer: TemplateRef<HTMLDivElement>;

    public faChevronDown = faChevronDown;
    public faChevronRight = faChevronRight;

    private toggleTime = 0;

    @Input()
    public open = false;

    @Input()
    public accordionInvalid = false;

    @Input()
    public group: string | undefined;

    @Input()
    public id: number | undefined = undefined;

    @Output()
    public openChange = new EventEmitter<boolean>();
    public constructor(
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly store: Store<FormsState>,
    ) {}

    public ngAfterContentChecked(): void {
        this.changeDetectorRef.markForCheck();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.accordionInvalid && isDefined(changes.accordionInvalid.currentValue)) {
            this.accordionInvalid = changes.accordionInvalid.currentValue as boolean;
        }
    }

    public toggleAccordion(event: UIEvent, element: HTMLElement): boolean {
        const now = Date.now();

        if (now - this.toggleTime < debounceTime) {
            event.preventDefault();

            if (typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }

            if (typeof event.stopImmediatePropagation === 'function') {
                event.stopImmediatePropagation();
            }
            return false;
        }
        this.toggleTime = now;
        this.changeDetectorRef.markForCheck();
        if (!this.open) {
            this.store.dispatch(
                expandAccordionByGroup({
                    groupId: `${this.getGroup()}`,
                }),
            );
            this.openChange.emit(true);
        } else {
            this.store.dispatch(
                collapseAccordionByGroup({
                    groupId: `${this.getGroup()}`,
                }),
            );
            this.openChange.emit(false);
            element.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'end',
            });
        }
        return true;
    }

    public expandSectionWithSubsections(): void {
        this.changeDetectorRef.markForCheck();

        // if an id is available then this is a content section
        if (isDefined(this.id)) {
            this.store.dispatch(
                expandAccordionWithChildren({
                    groupId: this.id,
                }),
            );
        }

        this.openChange.emit(true);
    }

    public collapseSectionWithSubsections(): void {
        this.changeDetectorRef.markForCheck();

        // if an id is available then this is a content section
        if (isDefined(this.id)) {
            this.store.dispatch(
                collapseAccordionWithChildren({
                    groupId: this.id,
                }),
            );
        }

        this.openChange.emit(false);
    }

    private getGroup(): string {
        return `${this.group}${isDefined(this.id) ? '-' + this.id.toString() : ''}`;
    }
}
